/*
 * Regras das telas de cobrança (BBF-158): situação da cobrança, o boleto que dá para pagar, o pagamento registrado
 * por fora e a régua de inadimplência. As regras que valem são as do app-financeiro; aqui só o que a tela precisa
 * para mostrar e para não mandar um pedido que a API vai recusar.
 */

export const COBRANCA_SITUACAO_LABELS = {
    ABERTA: 'Em aberto',
    RECUSADA: 'Recusada',
    PAGA: 'Paga',
    ESTORNADA: 'Estornada',
    CHARGEBACK: 'Contestada (chargeback)',
    CANCELADA: 'Cancelada',
};

// Filtros da lista: um valor por situação, como a API aceita, mais "Vencidas" (em aberto e vencida).
export const COBRANCA_FILTROS = [
    {valor: '', rotulo: 'Todas'},
    {valor: 'ABERTA', rotulo: 'Em aberto'},
    {valor: 'VENCIDAS', rotulo: 'Vencidas'},
    {valor: 'PAGA', rotulo: 'Pagas'},
    {valor: 'RECUSADA', rotulo: 'Recusadas'},
    {valor: 'CHARGEBACK', rotulo: 'Chargeback'},
    {valor: 'ESTORNADA', rotulo: 'Estornadas'},
    {valor: 'CANCELADA', rotulo: 'Canceladas'},
];

export const MEIO_DA_COBRANCA_LABELS = {BOLETO: 'Boleto com Pix', CARTAO: 'Cartão'};

// Em aberto para o financeiro: é o que ainda recebe pagamento (CobrancaService.EM_ABERTO).
export const SITUACOES_EM_ABERTO = ['ABERTA', 'RECUSADA', 'CHARGEBACK'];

// Boleto que ainda dá para pagar no banco.
const BOLETO_PAGAVEL = ['EMITIDO', 'REGISTRADO', 'VENCIDO'];

export const FORMAS_DO_PAGAMENTO_MANUAL = [
    {valor: 'PIX', rotulo: 'Pix fora do boleto'},
    {valor: 'TRANSFERENCIA', rotulo: 'Transferência'},
    {valor: 'MANUAL', rotulo: 'Outro meio'},
];

// Passos da régua, contados do vencimento (BBF-152, Regua.java do app-financeiro).
export const REGUA_PASSOS = [
    {situacao: 'EM_ATRASO', dias: 1, efeito: 'aviso de atraso; o acesso continua'},
    {situacao: 'SOMENTE_CONSULTA', dias: 15, efeito: 'só as telas de leitura'},
    {situacao: 'SUSPENSA', dias: 30, efeito: 'sem acesso'},
    {situacao: 'CANCELADA', dias: 60, efeito: 'assinatura encerrada'},
];

const EVENTO_LABELS = {
    REGISTRADA: 'Boleto registrado no banco',
    PAGA: 'Pagamento confirmado pelo banco',
    PAGA_MANUAL: 'Pagamento registrado por fora',
    BAIXADA: 'Boleto baixado no banco',
    PAGAMENTO_DESFEITO: 'Pagamento desfeito pelo banco',
    RETORNO_SEM_EFEITO: 'Retorno do banco sem efeito',
};

/**
 * Hoje no fuso de Brasília, como `aaaa-mm-dd`, para comparar com as datas da API sem cair na véspera.
 */
export const hojeIso = (agora = new Date()) => {
    const partes = new Intl.DateTimeFormat('en-CA', {timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit'})
        .formatToParts(agora)
        .reduce((acc, parte) => ({...acc, [parte.type]: parte.value}), {});
    return `${partes.year}-${partes.month}-${partes.day}`;
};

export const getMeioDaCobrancaLabel = (meio) => MEIO_DA_COBRANCA_LABELS[meio] || meio || '-';

export const emAberto = (cobranca) => SITUACOES_EM_ABERTO.includes(cobranca?.situacao);

export const vencida = (cobranca, hoje = hojeIso()) =>
    cobranca?.situacao === 'ABERTA' && Boolean(cobranca?.vencimento) && String(cobranca.vencimento).slice(0, 10) < hoje;

/**
 * Situação para mostrar: o rótulo da API, com "Vencida" no lugar de "Em aberto" depois do vencimento, e o tom do chip.
 */
export const situacaoDaCobranca = (cobranca, hoje = hojeIso()) => {
    if (vencida(cobranca, hoje)) return {rotulo: 'Vencida', tom: 'error'};
    const situacao = cobranca?.situacao;
    const rotulo = COBRANCA_SITUACAO_LABELS[situacao] || situacao || '-';
    if (situacao === 'PAGA') return {rotulo, tom: 'success'};
    if (situacao === 'ABERTA') return {rotulo, tom: 'info'};
    if (situacao === 'RECUSADA' || situacao === 'CHARGEBACK') return {rotulo, tom: 'warning'};
    return {rotulo, tom: 'default'};
};

/**
 * O boleto que a empresa ainda pode pagar: cobrança em aberto no boleto, com boleto emitido, registrado ou vencido.
 */
export const boletoParaPagar = (cobranca) => {
    const boleto = cobranca?.boleto;
    if (!emAberto(cobranca) || cobranca?.meio !== 'BOLETO' || !boleto) return null;
    return BOLETO_PAGAVEL.includes(boleto.status) ? boleto : null;
};

/**
 * A cobrança no boleto que ainda espera o registro no banco: em aberto e sem boleto pagável.
 */
export const boletoEmRegistro = (cobranca) =>
    emAberto(cobranca) && cobranca?.meio === 'BOLETO' && !boletoParaPagar(cobranca);

export const podeRegistrarPagamento = (cobranca) => emAberto(cobranca);

// Reenviar o boleto é mandar de novo o e-mail com a linha e o Pix; só faz sentido com boleto pagável.
export const podeReenviarBoleto = (cobranca) => Boolean(boletoParaPagar(cobranca));

export const getEventoLabel = (tipo) => {
    if (EVENTO_LABELS[tipo]) return EVENTO_LABELS[tipo];
    if (String(tipo || '').startsWith('AVISO_')) return 'Aviso enviado';
    return tipo || '-';
};

export const formularioDoPagamento = (cobranca, hoje = hojeIso()) => ({
    valorPago: cobranca?.valor != null ? String(cobranca.valor) : '',
    dataPagamento: hoje,
    formaPagamento: 'PIX',
    idTransacao: '',
    motivo: '',
});

/**
 * As mesmas recusas do app-financeiro, para a tela avisar antes: valor que não cobre a cobrança, data futura,
 * motivo vazio ou longo demais e identificador longo demais.
 */
export const validarPagamento = (form, cobranca, hoje = hojeIso()) => {
    const erros = {};
    const valor = Number(String(form?.valorPago ?? '').replace(',', '.'));
    if (!form?.valorPago || Number.isNaN(valor) || valor <= 0) {
        erros.valorPago = 'Informe o valor pago.';
    } else if (cobranca?.valor != null && valor < Number(cobranca.valor)) {
        erros.valorPago = 'O valor pago precisa cobrir a cobrança inteira. Pagamento parcial não é registrado.';
    }
    if (!form?.dataPagamento) {
        erros.dataPagamento = 'Informe a data do pagamento.';
    } else if (form.dataPagamento > hoje) {
        erros.dataPagamento = 'A data do pagamento não pode ser futura.';
    }
    if (!FORMAS_DO_PAGAMENTO_MANUAL.some((forma) => forma.valor === form?.formaPagamento)) {
        erros.formaPagamento = 'Escolha como foi pago.';
    }
    const motivo = String(form?.motivo || '').trim();
    if (!motivo) {
        erros.motivo = 'Conte por que o pagamento está sendo registrado à mão.';
    } else if (motivo.length > 500) {
        erros.motivo = 'Use até 500 caracteres.';
    }
    if (String(form?.idTransacao || '').trim().length > 120) {
        erros.idTransacao = 'Use até 120 caracteres.';
    }
    return erros;
};

export const corpoDoPagamento = (form) => {
    const idTransacao = String(form.idTransacao || '').trim();
    return {
        valorPago: Number(String(form.valorPago).replace(',', '.')),
        dataPagamento: form.dataPagamento,
        formaPagamento: form.formaPagamento,
        ...(idTransacao ? {idTransacao} : {}),
        motivo: String(form.motivo).trim(),
    };
};

/**
 * Parâmetros da lista de cobranças: a página da tela começa em 1, e a da API em 0.
 */
export const parametrosDaLista = ({filtro = '', meio = '', empresaId = '', pagina = 1, limite = 10} = {}) => ({
    ...(filtro === 'VENCIDAS' ? {vencidas: 'true'} : {}),
    ...(filtro && filtro !== 'VENCIDAS' ? {situacao: filtro} : {}),
    ...(meio ? {meio} : {}),
    ...(empresaId ? {empresa_id: empresaId} : {}),
    page: Math.max(0, pagina - 1),
    limit: limite,
});

export const normalizarPaginaDeCobrancas = (payload = {}) => ({
    itens: Array.isArray(payload?.data) ? payload.data : [],
    total: Number(payload?.total || 0),
    paginas: Number(payload?.totalPages || 0),
    pagina: Number(payload?.page || 0) + 1,
    limite: Number(payload?.limit || 10),
});

/**
 * O próximo passo da régua em linguagem da tela. Data de hoje ou passada quer dizer que acontece na próxima
 * execução diária, que roda de madrugada.
 */
export const textoDoProximoPasso = (proximoPasso, hoje = hojeIso()) => {
    if (!proximoPasso?.situacao || !proximoPasso?.data) return null;
    const data = String(proximoPasso.data).slice(0, 10);
    if (data <= hoje) return {quando: 'na próxima execução diária', atrasado: true};
    return {quando: null, atrasado: false};
};

/**
 * A fatura emitida no formato da prévia (BBF-24), para usar o mesmo componente: uma seção por assinatura, com as
 * linhas que o fechamento gravou. O texto da linha vem do snapshot (`referencia_detalhada`), como no PDF.
 */
export const composicaoDaFatura = (detalhe = {}) => ({
    secoes: (detalhe?.secoes || []).map((secao) => ({
        licenca_id: secao.licenca_id,
        plano: secao.plano_nome || 'Assinatura',
        versao: secao.versao_numero,
        chave: secao.chave_ativacao,
        meio: secao.meio_pagamento,
        tipo: secao.tipo,
        total: secao.total,
        linhas: (secao.itens || []).map((item) => ({
            tipo: item.tipo,
            o_que: item.referencia_detalhada?.o_que || item.descricao,
            explicacao: item.referencia_detalhada?.explicacao || null,
            valor: item.valor,
        })),
    })),
    fora: [],
    total: detalhe?.fatura?.valor_total,
});
